import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import 'd3-format'
import PopulationDistributionChart, {colors} from '../charts/PopulationDistribution'
import ChartContainer from '../ChartContainer'
import CustomBox from '../../common/CustomBox'
import {OutlinedInput, Typography} from '@mui/material'
import {useStatesAndHealthConditions} from '../../../store/hooks/breadCrumbs'
import {getDefaultState} from '../../../shared/helpers/getState'
import {StyledDiv, StyledCircleIcon, StyledSpan} from './Styles/CardStyling'
import {useDistributionDimension} from '../../../store/hooks/populationDistributionDimension'
import {useConfig} from '../../../store/hooks/config'
import FormGroup from '@mui/material/FormGroup'
import {useTableViewContext} from '../../../store/hooks/tableView'
import {classes} from '../DashboardStyle'
import EqualizerOutlinedIcon from '@mui/icons-material/EqualizerOutlined'
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined'
import ToggleButtonGroup from '../../common/ToggleButtonGroup/ToggleButtonGroup'
import {AnalyticsHandler} from '../../../shared/handlers/analytics/handlers'

const PopulationDistribution = () => {
  const {distributionDimensionList, selectedDimension, setSelectedDimension} = useDistributionDimension()
  const dimensions = distributionDimensionList?.populationDistributionDimensions || []
  const {filters} = useStatesAndHealthConditions()
  const selectedState = getDefaultState(filters)
  const {statesConfig} = useConfig() || {}

  const handleChange = (event) => {
    setSelectedDimension(event.target.value)
  }

  const {updateTogglePopulationDistribution, showPopulationDistributionTable} = useTableViewContext()
  const toggleBtnDetails = [
    {
      tooltipProps: {title: 'Chart View'},
      toggleBtnProps: {value: 'chart', 'aria-label': 'chart', icon: <EqualizerOutlinedIcon />}
    },
    {
      tooltipProps: {title: 'Table View'},
      toggleBtnProps: {value: 'table', 'aria-label': 'table', icon: <ViewListOutlinedIcon />}
    }
  ]
  const populationDistributionTitle = (
    <CustomBox>
      <Typography
        aria-hidden='true'
        id='select-label'
        aria-label={`What is the distribution of the selected population by-${selectedDimension}`}
        variant='kpiSmall'
        color='primary'
        component='span'
        sx={{
          fontSize: (theme) => theme.typography.h6.fontSize,
          fontWeight: (theme) => theme.typography.fontWeightSemibold,
          color: (theme) => theme.palette.hpBlack
        }}
      >
        What is the distribution of the selected population by &nbsp;
      </Typography>
      <FormControl variant='outlined' className={classes.dimensionSelectFormControl}>
        <Select
          labelId='select-label'
          classes={{select: classes.dimensionSelect}}
          value={selectedDimension || ''}
          onChange={handleChange}
          id='select'
          inputProps={{'aria-labelledby': 'select'}}
          input={
            <OutlinedInput
              size='small'
              sx={{
                width: '140px',
                height: '33px',
                padding: '0px'
              }}
            />
          }
          disableUnderline
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left'
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left'
            },
            PaperProps: {
              sx: {
                marginTop: '2px',
                maxHeight: '35%'
              }
            },
            autoFocus: true
          }}
        >
          {selectedDimension === 'none' && (
            <MenuItem value='none' disabled>
              Select a dimension
            </MenuItem>
          )}
          {Array.isArray(dimensions) &&
            dimensions?.map((dimension) => (
              <MenuItem key={`dimesion_${dimension}`} value={dimension}>
                {dimension}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      {selectedState?.map((obj, index) => {
        const color = colors[index % colors.length]
        const entireUnitedStatesLabel = selectedState.includes(statesConfig?.entireUnitedStateLabel)
        const filterLabel = entireUnitedStatesLabel ? 'Selected US Population' : obj
        const ariaLabel = `Selected state: ${filterLabel}`
        return (
          <StyledDiv key={obj}>
            <StyledCircleIcon style={{color}} />
            <StyledSpan tabIndex={0} aria-label={ariaLabel}>
              {filterLabel}
            </StyledSpan>
          </StyledDiv>
        )
      })}
      <FormGroup style={{display: 'inline', float: 'right', marginTop: '-4px'}}>
        <ToggleButtonGroup
          onSelectionSuccess={() => {
            AnalyticsHandler.onDashboardButtonClick({
              button: {name: showPopulationDistributionTable ? 'Table to Chart' : 'Chart to Table'}
            })
          }}
          toggleButtons={toggleBtnDetails}
          onSelection={updateTogglePopulationDistribution}
          defaultValue={showPopulationDistributionTable ? 'table' : 'chart'}
        />
      </FormGroup>
    </CustomBox>
  )

  return (
    <ChartContainer
      tabIndex={0}
      title={populationDistributionTitle}
      dimension={selectedDimension === 'none' ? '' : selectedDimension}
      chart={PopulationDistributionChart}
      chartName='PopulationDistributionChart'
    />
  )
}

export default PopulationDistribution
